import { FollowUpDrawer } from '@/components/follow-ups/follow-up-drawer'
import { FollowUpTable } from '@/components/follow-ups/follow-up-table'
import { HistoryTab } from '@/components/follow-ups/history-tab'
import { type Period, PeriodPicker, presetPeriod } from '@/components/follow-ups/period-picker'
import { STATUS_SEGMENTS } from '@/components/follow-ups/queue-meta'
import { QueueTiles } from '@/components/follow-ups/queue-tiles'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useFollowUpAssignees,
  useFollowUpSummary,
  useFollowUps,
  useGenerateFollowUps,
} from '@/hooks/useFollowUps'
import type { FollowUpQueue, FollowUpRow, FollowUpStatusFilter } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth-provider'
import { type BranchId, useBranch } from '@/providers/branch-provider'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, RefreshCw, Search } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/follow-ups')({
  component: FollowUpsPage,
})

const BRANCH_IDS: Record<BranchId, number | undefined> = {
  all: undefined,
  'shah-alam': 1,
  putrajaya: 2,
  bangi: 3,
  'kuala-lumpur': 4,
}

function FollowUpsPage() {
  const { hasRole } = useAuth()
  const { branchId } = useBranch()
  const branchIdNum = BRANCH_IDS[branchId]
  const canRefresh = hasRole(['admin', 'super_admin', 'branch_manager'])

  const [tab, setTab] = React.useState<'queue' | 'history'>('queue')
  const [period, setPeriod] = React.useState<Period>(() => presetPeriod('yesterday'))
  const [assignedTo, setAssignedTo] = React.useState<number | undefined>(undefined)
  const [selectedQueue, setSelectedQueue] = React.useState<FollowUpQueue | null>(null)
  const [statusSegment, setStatusSegment] = React.useState<FollowUpStatusFilter>('open')
  const [search, setSearch] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [page, setPage] = React.useState(1)
  const pageSize = 20

  const [drawerRow, setDrawerRow] = React.useState<FollowUpRow | null>(null)
  const [drawerOpen, setDrawerOpen] = React.useState(false)

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  React.useEffect(() => {
    setPage(1)
  }, [branchId, assignedTo, selectedQueue, statusSegment, period])

  const { data: summary, isPending: summaryPending } = useFollowUpSummary({
    branchId: branchIdNum,
    status: statusSegment,
    assignedTo,
    from: period.from,
    to: period.to,
  })
  const { data: assigneesRes } = useFollowUpAssignees(branchIdNum)
  const { data, isPending } = useFollowUps({
    branchId: branchIdNum,
    queue: selectedQueue ?? undefined,
    status: statusSegment,
    assignedTo,
    from: period.from,
    to: period.to,
    search: debouncedSearch || undefined,
    // CA work order: most time-sensitive queue first, then oldest queued row
    // (closest to expiry), then value. See QUEUE_ORDER in queue-meta.ts.
    sort: 'priority',
    page,
    pageSize,
  })
  const generateMutation = useGenerateFollowUps()

  const rows = data?.data ?? []

  const handleWhatsApp = React.useCallback((row: FollowUpRow) => {
    setDrawerRow(row)
    setDrawerOpen(true)
  }, [])

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Follow-ups"
        subtitle="Patients for CAs to contact. Updated nightly from Avixo data."
      />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4 overflow-y-auto">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={assignedTo ? String(assignedTo) : 'all'}
              onValueChange={(v) => setAssignedTo(v === 'all' ? undefined : Number(v))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="CA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All CAs</SelectItem>
                {assigneesRes?.data.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={generateMutation.isPending}
              onClick={() =>
                generateMutation.mutate(
                  { branchId: branchIdNum },
                  { onError: () => toast.error('Failed to refresh follow-up queues') }
                )
              }
            >
              {generateMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Refresh
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'queue' | 'history')}>
          <TabsList>
            <TabsTrigger value="queue">Follow-ups</TabsTrigger>
            <TabsTrigger value="history">History & report</TabsTrigger>
          </TabsList>

          <TabsContent value="queue" className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <PeriodPicker value={period} onChange={setPeriod} />
              <p className="text-xs text-text-muted">
                Date means: consult date (Consult), missed appointment date (No-show), last visit
                (package and lapsed queues). Stalled and lapsed patients have older visits, so they
                show under <strong>Any date</strong> or a longer custom range.
              </p>
            </div>

            <QueueTiles
              summary={summary}
              isPending={summaryPending}
              selected={selectedQueue}
              onSelect={setSelectedQueue}
            />

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg bg-muted p-1">
                {STATUS_SEGMENTS.map((seg) => (
                  <button
                    key={seg.key}
                    type="button"
                    onClick={() => setStatusSegment(seg.key)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-[12.5px] font-semibold',
                      statusSegment === seg.key
                        ? 'bg-card shadow-sm text-text-dark'
                        : 'text-text-muted'
                    )}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / PNO / package"
                  className="pl-8"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3.5">
              <FollowUpTable
                rows={rows}
                isPending={isPending}
                selectedId={drawerRow?.id ?? null}
                onSelect={(row) => {
                  setDrawerRow(row)
                  setDrawerOpen(true)
                }}
                onWhatsApp={handleWhatsApp}
              />
            </div>

            {data && data.total > pageSize && (
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-text-muted">
                  Showing {rows.length ? (page - 1) * pageSize + 1 : 0}–
                  {(page - 1) * pageSize + rows.length} of {data.total}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page * pageSize >= data.total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <HistoryTab branchId={branchIdNum} />
          </TabsContent>
        </Tabs>
      </div>

      <FollowUpDrawer
        row={drawerRow}
        branchId={branchIdNum}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}
